export interface LumpSumPayment {
  amount: number;
  month: number; // Month number (e.g., 12 for end of year 1)
}

export interface InterestRateChange {
  month: number;
  rate: number; // New interest rate as a percentage
}

export interface BondInputs {
  bondAmount: number;
  deposit: number;
  interestRate: number; // Annual percentage rate
  termYears: number;
  additionalMonthlyPayment: number;
  lumpSumPayments: LumpSumPayment[];
  interestRateChanges: InterestRateChange[];
}

export interface BondCalculationResult {
  principal: number; // Bond amount - deposit
  monthlyPayment: number;
  monthlyPaymentWithAdditional: number;
  totalMonths: number;
  totalInterestPaid: number;
  totalInterestPaidWithAdditional: number;
  totalAmountPaid: number;
  totalAmountPaidWithAdditional: number;
  savings: number; // Savings from additional payments
  payoffMonths: number; // Number of months to pay off with additional payments
  monthByMonthData?: MonthData[]; // Balance data by month for charting
}

export interface MonthData {
  month: number;
  standardBalance: number;
  additionalMonthlyBalance: number;
  lumpSumBalance: number;
}

/**
 * Calculate monthly bond payment using the standard mortgage formula:
 * M = P * [r(1+r)^n] / [(1+r)^n - 1]
 * Where:
 * M = Monthly payment
 * P = Principal (bond amount - deposit)
 * r = Monthly interest rate (annual rate / 12)
 * n = Total number of payments (term in years * 12)
 */
export function calculateMonthlyPayment(
  principal: number,
  annualInterestRate: number,
  termYears: number
): number {
  if (principal <= 0 || termYears <= 0) return 0;

  const monthlyRate = annualInterestRate / 100 / 12;
  const numberOfPayments = termYears * 12;

  if (monthlyRate === 0) {
    // If interest rate is 0, just divide principal by number of payments
    return principal / numberOfPayments;
  }

  const denominator = Math.pow(1 + monthlyRate, numberOfPayments) - 1;
  const monthlyPayment =
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
    denominator;

  return monthlyPayment;
}

/**
 * Get the interest rate for a specific month based on rate changes
 */
function getInterestRateForMonth(
  baseRate: number,
  rateChanges: InterestRateChange[],
  month: number
): number {
  // Start with the base rate
  let currentRate = baseRate;

  // Apply any rate changes that occur up to this month
  for (const change of rateChanges) {
    if (change.month > 0 && change.month <= month && change.rate > 0) {
      currentRate = change.rate;
    }
  }

  return currentRate;
}

/**
 * Calculate month-by-month balance for different repayment scenarios
 */
function calculateMonthByMonthData(
  principal: number,
  monthlyRate: number,
  monthlyPayment: number,
  totalMonths: number,
  additionalMonthlyPayment: number,
  lumpSumPayments: LumpSumPayment[],
  interestRateChanges: InterestRateChange[]
): MonthData[] {
  const data: MonthData[] = [];

  // Scenario 1: Standard payments only
  let standardBalance = principal;

  // Scenario 2: Additional monthly payments
  let additionalBalance = principal;

  // Scenario 3: Lump sum payments only (no additional monthly)
  let lumpSumBalance = principal;
  const lumpSumMap = new Map<number, number>();
  lumpSumPayments.forEach(({ month, amount }) => {
    if (month > 0 && amount > 0) {
      const existing = lumpSumMap.get(month) || 0;
      lumpSumMap.set(month, existing + amount);
    }
  });

  for (let month = 0; month <= totalMonths; month++) {
    // Get the interest rate for this month (could be variable)
    const currentAnnualRate = getInterestRateForMonth(
      monthlyRate * 12 * 100,
      interestRateChanges,
      month
    );
    const currentMonthlyRate = currentAnnualRate / 100 / 12;

    // Calculate standard balance
    if (month > 0) {
      const interest = standardBalance * currentMonthlyRate;
      standardBalance = standardBalance + interest - monthlyPayment;
      if (standardBalance < 0) standardBalance = 0;
    }

    // Calculate additional monthly balance
    if (month > 0) {
      const interest = additionalBalance * currentMonthlyRate;
      additionalBalance =
        additionalBalance +
        interest -
        monthlyPayment -
        additionalMonthlyPayment;
      if (additionalBalance <= 0) {
        additionalBalance = 0;
      }
    }

    // Calculate lump sum only balance
    if (month > 0) {
      const interest = lumpSumBalance * currentMonthlyRate;

      lumpSumBalance = lumpSumBalance + interest - monthlyPayment;

      // Add lump sum if applicable
      if (lumpSumMap.has(month)) {
        lumpSumBalance -= lumpSumMap.get(month) || 0;
      }

      if (lumpSumBalance < 0) lumpSumBalance = 0;
    }

    data.push({
      month,
      standardBalance: Math.max(0, standardBalance),
      additionalMonthlyBalance: Math.max(0, additionalBalance),
      lumpSumBalance: Math.max(0, lumpSumBalance),
    });
  }

  return data;
}

/**
 * Calculate the complete bond repayment schedule with additional payments
 */
export function calculateBondRepayment(
  inputs: BondInputs
): BondCalculationResult {
  const principal = inputs.bondAmount - inputs.deposit;

  if (principal <= 0 || inputs.termYears <= 0) {
    return {
      principal: 0,
      monthlyPayment: 0,
      monthlyPaymentWithAdditional: 0,
      totalMonths: 0,
      totalInterestPaid: 0,
      totalInterestPaidWithAdditional: 0,
      totalAmountPaid: 0,
      totalAmountPaidWithAdditional: 0,
      savings: 0,
      payoffMonths: 0,
    };
  }

  const monthlyRate = inputs.interestRate / 100 / 12;
  const totalMonths = inputs.termYears * 12;

  // Calculate standard monthly payment
  const monthlyPayment = calculateMonthlyPayment(
    principal,
    inputs.interestRate,
    inputs.termYears
  );

  // Calculate with additional payments
  let remainingBalance = principal;
  let totalInterestPaid = 0;
  let totalInterestPaidWithAdditional = 0;
  let currentMonth = 1;
  let payoffMonths = totalMonths;

  // Track which lump sums to apply (convert to map for easier lookup)
  const lumpSumMap = new Map<number, number>();
  inputs.lumpSumPayments.forEach(({ month, amount }) => {
    if (month > 0 && amount > 0) {
      const existing = lumpSumMap.get(month) || 0;
      lumpSumMap.set(month, existing + amount);
    }
  });

  // Calculate standard scenario (no additional payments)
  totalInterestPaid = monthlyPayment * totalMonths - principal;

  // Calculate with additional payments and lump sums
  while (remainingBalance > 0 && currentMonth <= totalMonths) {
    const interestPayment = remainingBalance * monthlyRate;
    let principalPayment = monthlyPayment - interestPayment;

    // Add additional monthly payment
    if (inputs.additionalMonthlyPayment > 0) {
      principalPayment += inputs.additionalMonthlyPayment;
    }

    // Add lump sum if applicable
    if (lumpSumMap.has(currentMonth)) {
      principalPayment += lumpSumMap.get(currentMonth) || 0;
    }

    totalInterestPaidWithAdditional += interestPayment;
    remainingBalance -= principalPayment;

    if (remainingBalance <= 0) {
      payoffMonths = currentMonth;
      break;
    }

    currentMonth++;
  }

  const totalAmountPaid = monthlyPayment * totalMonths;
  const totalAmountPaidWithAdditional =
    (monthlyPayment + inputs.additionalMonthlyPayment) * payoffMonths;

  // Calculate month-by-month data for the chart
  const monthByMonthData = calculateMonthByMonthData(
    principal,
    monthlyRate,
    monthlyPayment,
    totalMonths,
    inputs.additionalMonthlyPayment,
    inputs.lumpSumPayments,
    inputs.interestRateChanges
  );

  return {
    principal,
    monthlyPayment,
    monthlyPaymentWithAdditional:
      monthlyPayment + inputs.additionalMonthlyPayment,
    totalMonths,
    totalInterestPaid,
    totalInterestPaidWithAdditional,
    totalAmountPaid,
    totalAmountPaidWithAdditional,
    savings: totalInterestPaid - totalInterestPaidWithAdditional,
    payoffMonths,
    monthByMonthData,
  };
}
